import styles from './template.css';
import template from './template';
import {AoflElement, customElement, property} from '@aofl/element';
import {i18nMixin} from '@aofl/i18n-mixin';
import {validationMixin, isRequired} from '@aofl/form-validate';
import translations from '../../i18n';
import {sdoNs, permissions, modals, exceptionCodes, objectTypes} from '@modules/constants-enumerate';
import {emailLike, phoneLike} from '@modules/custom-form-validators';
import '@modules/sdo-teacher-info';
import {storeInstance} from '@aofl/store/dist/legacy';
import {SimpleModal} from '@modules/simple-modal';
import {Toast} from '@modules/toast';
import {ApiService} from '@modules/api-service';
import {toastStatus} from '@modules/i18n-labels';
import {hasOneOfPermissions} from '@modules/permissions';
import {userJobs} from '@modules/user-jobs';
import {SageUtils} from '@modules/sage-utils';

/**
 * @summary TeacherEditForm
 * @extends {validationMixin(i18nMixin(AoflElement))}
 */
@customElement('teacher-edit-form')
class TeacherEditForm extends validationMixin(i18nMixin(AoflElement)) {
  /**
   * Creates an instance of TeacherEditForm.
   */
  constructor() {
    super();
    this.translations = translations;
    this.validators = {
      firstName: {isRequired},
      parentId: {
        required: (value) => {
          return value !== 0;
        }
      },
      email: {
        emailLike,
        conflicting: (value) => {
          return !value || !this.conflictingEmail || value !== this.conflictingEmail;
        },
      },
      contactEmail: {
        emailLike,
      },
      lastName: {temp: () => true},
      phone: {
        phoneLike,
      },
      contactPhone: {
        phoneLike,
      },
      isThereEmailOrPhone: {
        emailOrPhone: this.emailOrPhone.bind(this),
      },
      externalId: {
        duplicate: (value) => {
          return !value || !this.duplicateExternalId || value !== this.duplicateExternalId;
        }
      },
      preferredCommunication: {temp: () => true},
      $classesGroupedBySchool: {temp: () => true},
    };
  }

  emailOrPhone() {
    // If we are creating a teacher, only check contact email/phone, else check all of them
    return isRequired(this.contactEmail) || isRequired(this.contactPhone);
  }

  /**
   * @readonly
   */
  static is = 'teacher-edit-form';
  formData = null;

  @property({type: String, mapState: `${sdoNs.TEACHER_INFO}.id`, store: storeInstance})
  teacherId = '';

  @property({type: String, mapState: `${sdoNs.TEACHER_INFO}.first_name`, store: storeInstance})
  firstName = '';

  @property({type: String, mapState: `${sdoNs.TEACHER_INFO}.last_name`, store: storeInstance})
  lastName = '';

  @property({type: String, mapState: `${sdoNs.TEACHER_INFO}.external_id`, store: storeInstance})
  externalId = '';

  @property({type: String, mapState: `${sdoNs.TEACHER_INFO}.preferred_communication`, store: storeInstance})
  preferredCommunication = '';

  @property({type: String, mapState: `${sdoNs.TEACHER_INFO}.phone`, store: storeInstance})
  phone = '';

  @property({type: String, mapState: `${sdoNs.TEACHER_INFO}.email`, store: storeInstance})
  email = '';

  @property({type: String, mapState: `${sdoNs.TEACHER_INFO}.contact_phone`, store: storeInstance})
  contactPhone = '';

  @property({type: String, mapState: `${sdoNs.TEACHER_INFO}.contact_email`, store: storeInstance})
  contactEmail = '';

  @property({type: Object, mapState: `${sdoNs.TEACHER_INFO}.$groupInfo`, store: storeInstance})
  groupInfo = {};

  @property({type: Boolean})
  saving = false;

  @property({type: Array, mapState: `${sdoNs.TEACHER_INFO}.classes`, store: storeInstance})
  classes = [];

  @property({type: Boolean, mapState: `${sdoNs.SESSION}.settings.teacher_login_enabled`, store: storeInstance})
  teacherLoginEnabled = false;

  @property({type: Array, mapState: `${sdoNs.TEACHER_INFO}.$classesGroupedBySchool`, store: storeInstance})
  $classesGroupedBySchool = [];

  @property({type: Array, attribute: false, mapState: `${sdoNs.TEACHER_INFO}.$products`, store: storeInstance})
  products = [];

  @property({type: Boolean, mapState: `${sdoNs.TEACHER_INFO}.$qrEnabled`, store: storeInstance})
  qrEnabled = false;

  @property({type: Object, mapState: `${sdoNs.SESSION}.third_party_connected_group`, store: storeInstance})
  thirdPartyConnectedGroup = null;

  @property({type: Object, mapState: `${sdoNs.SESSION}.group_info`, store: storeInstance})
  sessionGroupInfo = {};

  @property({type: String, mapState: `${sdoNs.SESSION}.locale`, store: storeInstance})
  locale = '';

  showEmptyClassSelection = false;

  another = false;

  @property({type: Boolean})
  disabled = false;

  @property({type: Boolean})
  hasPerms = false;

  conflictingEmail = '';

  reset() {
    this.saving = false;
    this.form.reset();
    storeInstance.commit({
      namespace: sdoNs.TEACHER_INFO,
      mutationId: 'init'
    });
    this.requestUpdate();
    this.dispatchValidateEvent();
  }

  dataUpdated(e) {
    if (this.disabled) {
      return;
    }
    if (e.detail.action === 'remove') {
      storeInstance.commit({
        namespace: sdoNs.TEACHER_INFO,
        mutationId: 'removeClass',
        payload: e.detail.classInfo.index
      });
    } else {
      storeInstance.commit(
        {
          namespace: sdoNs.TEACHER_INFO,
          mutationId: 'addGroups',
          payload: e.detail.selectedGroups,
        },
        {
          namespace: sdoNs.TEACHER_INFO,
          mutationId: 'addClass',
          payload: e.detail.class,
        }
      );
      this.showEmptyClassSelection = false;
    }

    this.form.$classesGroupedBySchool.validate();
    this.dispatchValidateEvent();
  }

  firstNameUpdated(e) {
    this.firstName = e.target.value.trim();
    storeInstance.commit({
      namespace: sdoNs.TEACHER_INFO,
      mutationId: 'setFirst_name',
      payload: this.firstName
    });
    this.form.firstName.validate();
    this.dispatchValidateEvent();
  }

  lastNameUpdated(e) {
    this.lastName = e.target.value.trim();
    storeInstance.commit({
      namespace: sdoNs.TEACHER_INFO,
      mutationId: 'setLast_name',
      payload: this.lastName
    });
    this.form.lastName.validate();
    this.dispatchValidateEvent();
  }

  externalIdUpdated(e) {
    this.externalId = e.target.value.trim();
    storeInstance.commit({
      namespace: sdoNs.TEACHER_INFO,
      mutationId: 'setExternal_id',
      payload: this.externalId
    });
  }

  async preferredCommunicationUpdated(e) {
    this.preferredCommunication = e.target.value.trim();
    storeInstance.commit({
      namespace: sdoNs.TEACHER_INFO,
      mutationId: 'setPreferred_communication',
      payload: this.preferredCommunication
    });
    this.form.preferredCommunication.validate();
    this.dispatchValidateEvent();

    if (!this.disabled) return;
    try {
      Toast.dismissAll();
      await ApiService.teacherUpdatePreferredCommunication(this.teacherId, this.preferredCommunication);
      Toast.success('Teacher preferred communication updated');
    } catch (e) {
      Toast.error(toastStatus.oops);
    }
  }

  emailUpdated(e) {
    this.email = e.target.value.trim();
    storeInstance.commit({namespace: sdoNs.TEACHER_INFO, mutationId: 'setEmail', payload: this.email});
    this.form.email.validate();
    this.form.isThereEmailOrPhone.validate();
    this.dispatchValidateEvent();
  }

  async downloadQRCode() {
    userJobs.pause();
    await ApiService.generateQr(
      {
        object_ids: [this.teacherId],
        object_type: objectTypes.USER
      }
    );
    userJobs.getJobs();
  }

  downloadEducatorCenterQRCode() {
    const params = new URLSearchParams();
    params.append(SageUtils.getFilterMapping('root_id'), this.sessionGroupInfo.root_id);
    params.append(SageUtils.getFilterMapping('object_id'), this.teacherId);
    params.append(SageUtils.getFilterMapping('group_path'), this.sessionGroupInfo.path + this.sessionGroupInfo.id + '.');
    params.append('locale', this.locale);
    return ApiService.getGenerateUserQRLogin(params.toString());
  }

  contactEmailUpdated(e) {
    this.contactEmail = e.target.value.trim();
    storeInstance.commit({namespace: sdoNs.TEACHER_INFO, mutationId: 'setContact_email', payload: this.contactEmail});
    this.form.contactEmail.validate();
    this.form.isThereEmailOrPhone.validate();
    this.dispatchValidateEvent();
  }

  setLoginModal(email, phone) {
    this.emailFromModal = email;
    this.phoneFromModal = phone;
  }

  phoneUpdated(e) {
    this.phone = e.target.value.trim();
    storeInstance.commit({namespace: sdoNs.TEACHER_INFO, mutationId: 'setPhone', payload: this.phone});
    this.form.phone.validate();
    this.form.isThereEmailOrPhone.validate();
    this.dispatchValidateEvent();
  }

  contactPhoneUpdated(e) {
    this.contactPhone = e.target.value.trim();
    storeInstance.commit({namespace: sdoNs.TEACHER_INFO, mutationId: 'setContact_phone', payload: this.contactPhone});
    this.form.contactPhone.validate();
    this.form.isThereEmailOrPhone.validate();
    this.dispatchValidateEvent();
  }

  async dispatchValidateEvent() {
    await this.form.validateComplete;
    this.dispatchEvent(new CustomEvent('validate', {detail: {phone: this.phone, contactPhone: this.contactPhone}}));
  }

  get confirmLoginChange() {
    if ((this.contactEmail !== this.emailFromModal) && (typeof this.emailFromModal !== 'undefined')) {
      return true;
    }
    if ((this.contactPhone !== this.phoneFromModal) && (typeof this.phoneFromModal !== 'undefined')) {
      return true;
    }
    return false;
  }

  async submit(e) {
    this.saving = true;
    e.preventDefault();

    const classIds = this.classes.map((classInfo) => classInfo.id)
      .join(',');

    const form = e.target;
    this.formData = new FormData(form);
    this.formData.set('teacher-id', this.teacherId);
    this.formData.set('class-ids', classIds);
    this.formData.set('phone', this.phone);

    this.form.validate();
    await this.form.validationComplete;

    if (this.form.valid) {
      let save = true;
      if (this.teacherLoginEnabled && (
        (this.ogEmail && this.email && this.email !== this.ogEmail) ||
        (this.ogPhone && this.phone && this.phone !== this.ogPhone)
      )) {
        save = await new Promise((resolve) => SimpleModal.show(modals.CONFIRM_TEACHER_LOGIN_CHANGE, {resolve}));
      }

      if (save) {
        this.dispatchEvent(new CustomEvent('edit-teacher-submit', {
          bubbles: true,
          composed: true,
        }));

        this.ogEmail = this.email;
        this.ogPhone = this.phone;
      }
    } else {
      this.saving = false;
    }
  }

  async setErrors(errors) {
    this.saving = false;

    if (!errors) {
      return;
    }

    if (
      errors.code === exceptionCodes.INPUT_CONFLICTING_PRODUCT_CREDENTIALS ||
      (errors.code === exceptionCodes.INPUT_GENERAL && typeof errors.payload[0]?.email?.duplicate !== 'undefined')
    ) {
      this.conflictingEmail = this.email;
    }

    if (errors.code === exceptionCodes.INPUT_GENERAL) {
      if (typeof errors.payload[0]?.external_id?.duplicate !== 'undefined') {
        this.duplicateExternalId = this.externalId;
      }
    }

    this.form.validate();
    await this.form.validationComplete;
  }

  connectedCallback() {
    super.connectedCallback();

    const state = storeInstance.getState();
    if (
      (this.thirdPartyConnectedGroup && state[sdoNs.TEACHER_INFO].managed_by) ||
      !hasOneOfPermissions([permissions.TEACHER_EDIT])
    ) {
      this.disabled = true;
      this.hasPerms = (hasOneOfPermissions([permissions.TEACHER_EDIT]));
    }

    this.ogEmail = this.email;
    this.ogPhone = this.phone;
  }

  /**
   *
   * @return {Object}
   */
  render() {
    return super.render({
      default: {
        template,
        styles: [styles]
      }
    });
  }
}

export default TeacherEditForm;
